/* Reviews row. Desktop: CSS marquee, nothing to do here for motion. Phones (<=989px): the same continuous drift, driven
   here so the row can still be swiped. The track scrolls itself ~35px/s and wraps onto the second copy of the cards
   without a visible jump; a touch, drag, wheel or keyboard focus hands the row to the reader, the drift resumes 2.5 s
   after they let go, and the row holds while any review is open. Reduced motion: no drift (the row stays swipeable).
   Reviews show three lines closed. A longer review is cut at the last WHOLE word that fits with "... Read more" on the
   third line (verifier 2026-09-15: a fade left half a letter), the words stay verbatim, and "Read more" opens the rest
   in place ("Show less" closes it). Recomputed when the row changes width. */
class SaltyReviewCarousel extends HTMLElement {
  static small = new Set(('a an the and or but so nor yet if as at by for from in into of on onto to with about than then ' +
    'when while i im ive id it its my me we our you your he she they their this that is was are were be been am has have ' +
    'had do did not no just very really').split(' ').concat(["i'm", "i've", "i'd", "it's", "don't", "didn't", "can't"]));
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.track = this.querySelector('.salty-reviews__grid');
    this.mobile = matchMedia('(max-width: 989px)');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.speed = 0.035; // px per ms
    this.heldUntil = 0;
    this.visible = false;
    this.pos = 0;
    this.last = 0;
    const hold = () => { this.heldUntil = Infinity; };
    const release = () => { this.heldUntil = performance.now() + 2500; };
    this.track.addEventListener('touchstart', hold, { signal, passive: true });
    this.track.addEventListener('touchend', release, { signal, passive: true });
    this.track.addEventListener('touchcancel', release, { signal, passive: true });
    this.track.addEventListener('pointerdown', event => { if (event.pointerType !== 'touch') hold(); }, { signal });
    window.addEventListener('pointerup', event => { if (event.pointerType !== 'touch' && this.heldUntil === Infinity) release(); }, { signal });
    this.track.addEventListener('wheel', release, { signal, passive: true });
    this.track.addEventListener('focusin', () => { if (this.track.matches(':focus-visible')) hold(); }, { signal });
    this.track.addEventListener('focusout', release, { signal });
    this.seen = new IntersectionObserver(entries => { this.visible = entries.some(e => e.isIntersecting); }, { rootMargin: '100px' });
    this.seen.observe(this.track);
    (document.fonts ? document.fonts.ready : Promise.resolve()).then(() => { if (!signal.aborted) this.fitAll(); });
    let width = this.track.clientWidth;
    this.resizer = new ResizeObserver(() => {
      const w = this.track.clientWidth;
      if (Math.abs(w - width) > 1) { width = w; this.fitAll(); }
    });
    this.resizer.observe(this.track);
    this.frame = requestAnimationFrame(t => this.loop(t));
  }
  fitAll() {
    this.track.querySelectorAll('.salty-review').forEach(card => { if (!card.classList.contains('is-open')) this.fit(card); });
  }
  // one button per card for its whole life: the text rewrites detach it, so it is kept on the card, not looked up in it
  // (a lookup after a rewrite found nothing, built a second button and left keyboard focus on the detached one)
  button(card) {
    if (!card.saltyMore) {
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'salty-review__more';
      if (card.getAttribute('aria-hidden') === 'true') more.tabIndex = -1;
      more.addEventListener('click', () => this.toggle(card), { signal: this.abort.signal });
      card.saltyMore = more;
    }
    return card.saltyMore;
  }
  // Longest run of whole words that fits three lines with "... Read more" after it (binary search over word count).
  fit(card) {
    const text = card.querySelector('.salty-review__text');
    if (!text) return;
    if (text.dataset.full === undefined) text.dataset.full = text.textContent.trim();
    const full = text.dataset.full;
    text.textContent = full;
    if (text.scrollHeight <= text.clientHeight + 1) return; // the whole review fits
    const more = this.button(card);
    more.textContent = 'Read more';
    const words = full.split(/\s+/);
    const tryWords = n => {
      text.textContent = words.slice(0, n).join(' ').replace(/[\s,.;:!?-]+$/, '') + '… ';
      text.append(more);
      return text.scrollHeight <= text.clientHeight + 1;
    };
    let lo = 1, hi = words.length - 1, best = 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (tryWords(mid)) { best = mid; lo = mid + 1; } else { hi = mid - 1; }
    }
    // never stop on a dangling small word ("so I...", "my gut at...", "And it's..."): back off to the last word that
    // carries meaning (round-9 verifier, 2026-09-15)
    const bare = w => w.toLowerCase().replace(/[’]/g, "'").replace(/[^a-z']/g, '');
    while (best > 4 && SaltyReviewCarousel.small.has(bare(words[best - 1]))) best--;
    // and never on a one- or two-word start of a new sentence ("...at all. Best..."): end on the sentence before it.
    // A short sentence that is already whole ("...my walk.. felt great!!!") stays: that is the line that sells.
    const fragment = words.slice(0, best).join(' ').match(/[.!?]+["”)]?\s+((?:\S+\s+)?\S+)$/);
    if (fragment && !/[.!?]["”)]?$/.test(fragment[1])) {
      const drop = fragment[1].split(/\s+/).length;
      if (best - drop >= 4) best -= drop;
    }
    tryWords(best);
  }
  toggle(card) {
    const text = card.querySelector('.salty-review__text');
    const more = this.button(card);
    const focused = document.activeElement === more; // rewriting the text detaches the button; give focus back after
    if (card.classList.toggle('is-open')) {
      text.textContent = text.dataset.full + ' ';
      more.textContent = 'Show less';
      text.append(more);
    } else {
      this.fit(card);
    }
    this.heldUntil = performance.now() + 8000;
    // the row holds while any review is open, so it never drifts away mid-read
    this.classList.toggle('has-open', !!this.track.querySelector('.salty-review.is-open'));
    if (focused) more.focus({ preventScroll: true });
  }
  loop(t) {
    this.frame = requestAnimationFrame(n => this.loop(n));
    const dt = Math.min(64, t - (this.last || t));
    this.last = t;
    if (!this.mobile.matches || this.reducedMotion.matches || document.hidden || !this.visible) return;
    if (t < this.heldUntil || this.classList.contains('has-open')) { this.pos = this.track.scrollLeft; return; }
    const first = this.track.querySelector('.salty-review');
    const copy = this.track.querySelector('.salty-review[aria-hidden="true"]');
    if (!first || !copy) return;
    const span = copy.offsetLeft - first.offsetLeft;
    this.pos += this.speed * dt;
    if (this.pos >= span) this.pos -= span;
    this.track.scrollLeft = this.pos;
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.seen?.disconnect();
    this.resizer?.disconnect();
    cancelAnimationFrame(this.frame);
    this.abort = null;
  }
}
if (!customElements.get('salty-review-carousel')) customElements.define('salty-review-carousel', SaltyReviewCarousel);
